import { supabase } from '@/shared/lib/supabase';

/**
 * adminDomainService — staff-side domain reads.
 *
 * - `listAll()`         reads `v_admin_expiring_domains` (RLS gives staff
 *                       full cross-customer access via is_staff()).
 * - `getByName(domain)` calls `domain-info` EF; the EF allows staff to
 *                       fetch any customer's domain via the role check.
 * - `getBalance()`      calls `domain-balance` EF (staff-only, AD-25).
 */

export type AdminDomainRow = {
  id: string;
  domain_name: string;
  customer_id: string;
  customer_name: string;
  customer_email: string | null;
  registrar: string;
  linked_at: string;
  /** From `domain_link.expiry_date` — the column the bulk registrar refresh
   *  writes, and the same one the Domains coverage report reads. Until
   *  2026-08-31 the view sourced this from a `domains_cache` key that has never
   *  existed, so it was NULL for every domain and the list showed all 23 as
   *  status "Unknown". */
  expiry_date: string | null;
  days_to_expiry: number | null;
  /** From `domain_link.auto_renew`. 13 of 23 live domains have it off. */
  auto_renew: boolean;
};

export type ContactPayload = {
  name?: string;
  email?: string;
  contactNumber?: string;
  address1?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  organisation?: string;
};

/** One open Terms 6.4(c) recoupment case from `v_admin_domain_recoupment_due`. */
export type RecoupmentCase = {
  domain_name: string;
  customer_id: string;
  customer_email: string | null;
  customer_name: string | null;
  hosting_activated_on: string;
  hosting_ended_on: string;
  minimum_term_ends_on: string;
  days_short_of_minimum_term: number;
  amount_due: number;
  price_needs_checking: boolean;
  service_order_id: string;
  domain_link_id: string | null;
};

export type RecoupmentDecision =
  | { ok: true; action: 'invoiced'; domain_name: string; invoice_id: string; invoice_number: string; amount: number; emailed: boolean }
  | { ok: true; action: 'waived'; domain_name: string; amount_waived: number };

/** Per-TLD retail + wholesale from `system_setting.domains_pricing` (staff-readable). */
export type TldPricingRow = {
  tld: string;
  register: number;
  renew: number;
  transfer: number;
  wholesale: number | null;
  renew_wholesale: number | null;
  wholesale_at: string | null;
  margin_rule: string | null;
};

export type AdminDomainBalance = {
  balance: number;
  currency: 'ZAR';
  accountType: string;
  lowBalance: boolean;
  balanceFloor: number;
  isBelowFloor: boolean;
  fetchedAt: string;
  fromCache: boolean;
};

export const adminDomainService = {
  /**
   * Open recoupment cases. The view is the single definition of eligibility
   * (free bundled domain, hosting ended inside the 12-month Minimum Term, not
   * yet invoiced or waived). Runs as owner; staff-gated. Until 2026-09-25 it
   * had no screen — the F4b trap (a correct view nobody looks at).
   */
  async listRecoupmentDue(): Promise<RecoupmentCase[]> {
    const { data, error } = await supabase.from('v_admin_domain_recoupment_due').select('*');
    if (error) throw new Error(`Could not load recoupment cases: ${error.message}`);
    return (data ?? []) as RecoupmentCase[];
  },

  /** Terms 6.4(c) invoice or 6.4(d) waiver. Full-staff only (the EF enforces it). */
  async decideRecoupment(domainName: string, action: 'invoice' | 'waive', reason?: string): Promise<RecoupmentDecision> {
    const { data, error } = await supabase.functions.invoke<RecoupmentDecision>('domain-recoupment-invoice', {
      method: 'POST',
      body: { domain_name: domainName, action, reason },
      headers: { 'idempotency-key': crypto.randomUUID() },
    });
    if (error) {
      const body = (error as { context?: { body?: { message?: string; error?: string } } }).context?.body;
      throw new Error(body?.message || body?.error || (error as { message?: string }).message || 'Recoupment decision failed.');
    }
    return data as RecoupmentDecision;
  },

  /**
   * Retail vs wholesale per TLD. `wholesale` / `wholesale_at` / `margin_rule`
   * have been stored since 2026-09-13; nothing displayed them (gap G11).
   */
  async listTldPricing(): Promise<TldPricingRow[]> {
    const { data, error } = await supabase
      .from('system_setting')
      .select('value')
      .eq('key', 'domains_pricing')
      .maybeSingle();
    if (error) throw new Error(`Could not load domain pricing: ${error.message}`);
    const table = (data?.value ?? {}) as Record<string, Partial<Record<keyof Omit<TldPricingRow, 'tld'>, unknown>>>;
    return Object.entries(table)
      .map(([tld, v]) => ({
        tld,
        register: Number(v.register ?? 0),
        renew: Number(v.renew ?? 0),
        transfer: Number(v.transfer ?? 0),
        wholesale: v.wholesale == null ? null : Number(v.wholesale),
        renew_wholesale: v.renew_wholesale == null ? null : Number(v.renew_wholesale),
        wholesale_at: (v.wholesale_at as string | undefined) ?? null,
        margin_rule: (v.margin_rule as string | undefined) ?? null,
      }))
      .sort((a, b) => a.tld.localeCompare(b.tld));
  },

  async listAll(): Promise<AdminDomainRow[]> {
    const { data, error } = await supabase
      .from('v_admin_expiring_domains')
      .select(
        'id, domain_name, customer_id, customer_name, customer_email, registrar, linked_at, expiry_date, days_to_expiry, auto_renew',
      );
    if (error) throw new Error(`Could not load domains: ${error.message}`);
    return (data ?? []) as AdminDomainRow[];
  },

  async importFromRegistrar(opts?: { dryRun?: boolean; fallbackCustomerId?: string }): Promise<{
    ok: boolean;
    dry_run: boolean;
    total_at_registrar: number;
    already_linked: number;
    newly_linked: number;
    unmatched_count: number;
    linked: Array<{ domain: string; customer_id: string; customer_email: string; expiry_date: string }>;
    unmatched: Array<{ domain: string; registrant_email: string | null; reason: string }>;
  }> {
    const { data, error } = await supabase.functions.invoke('domain-import-from-registrar', {
      method: 'POST',
      body: {
        dry_run: opts?.dryRun ?? false,
        fallback_customer_id: opts?.fallbackCustomerId,
      },
    });
    if (error) throw new Error(`Import failed: ${(error as { message?: string }).message ?? error}`);
    return data;
  },

  async getBalance(): Promise<AdminDomainBalance> {
    const { data, error } = await supabase.functions.invoke<AdminDomainBalance>(
      'domain-balance',
      { method: 'GET' },
    );
    if (error) throw new Error(`Could not load balance: ${(error as { message?: string }).message ?? error}`);
    if (!data) throw new Error('Balance check returned empty response.');
    return data;
  },

  async bulkRefresh(): Promise<{
    ok: boolean;
    total_links: number;
    total_at_registrar: number;
    updated_count: number;
    missing_count: number;
    refreshed_at: string;
  }> {
    const { data, error } = await supabase.functions.invoke('domain-bulk-refresh', { method: 'POST' });
    if (error) throw new Error(`Refresh failed: ${(error as { message?: string }).message ?? error}`);
    return data;
  },

  async renew(domainLinkId: string, periodYears = 1): Promise<{ invoice_id: string; invoice_number: string; total_zar: number; pay_url: string }> {
    const idempotencyKey = crypto.randomUUID();
    const { data, error } = await supabase.functions.invoke('domain-renew', {
      method: 'POST',
      body: { domain_link_id: domainLinkId, period_years: periodYears },
      headers: { 'idempotency-key': idempotencyKey },
    });
    if (error) {
      const body = (error as { context?: { body?: { message?: string } } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Renew failed.');
    }
    return data;
  },

  async toggleAutoRenew(domainLinkId: string, enabled: boolean): Promise<{ domain: string; auto_renew: boolean }> {
    const idempotencyKey = crypto.randomUUID();
    const { data, error } = await supabase.functions.invoke('domain-autorenew', {
      method: 'POST',
      body: { domain_link_id: domainLinkId, enabled },
      headers: { 'idempotency-key': idempotencyKey },
    });
    if (error) {
      const body = (error as { context?: { body?: { message?: string } } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Toggle failed.');
    }
    return data;
  },

  async setLock(domainLinkId: string, action: 'lock' | 'unlock'): Promise<{ domain: string; action: string }> {
    const idempotencyKey = crypto.randomUUID();
    const { data, error } = await supabase.functions.invoke('domain-lock', {
      method: 'POST',
      body: { domain_link_id: domainLinkId, action },
      headers: { 'idempotency-key': idempotencyKey },
    });
    if (error) {
      const body = (error as { context?: { body?: { message?: string } } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Lock failed.');
    }
    return data;
  },

  async updateNameservers(domainLinkId: string, nameservers: string[]): Promise<{ domain: string; nameservers: string[] }> {
    const idempotencyKey = crypto.randomUUID();
    const { data, error } = await supabase.functions.invoke('domain-update-ns', {
      method: 'POST',
      body: { domain_link_id: domainLinkId, nameservers },
      headers: { 'idempotency-key': idempotencyKey },
    });
    if (error) {
      const body = (error as { context?: { body?: { message?: string } } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'NS update failed.');
    }
    return data;
  },

  async updateContacts(
    domainLinkId: string,
    contacts: {
      registrant?: ContactPayload;
      admin?: ContactPayload;
      tech?: ContactPayload;
      billing?: ContactPayload;
    },
  ): Promise<{ domain: string; roles_updated: string[] }> {
    const idempotencyKey = crypto.randomUUID();
    const { data, error } = await supabase.functions.invoke('domain-update-contact', {
      method: 'POST',
      body: { domain_link_id: domainLinkId, contacts },
      headers: { 'idempotency-key': idempotencyKey },
    });
    if (error) {
      const body = (error as { context?: { body?: { message?: string } } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'Contact update failed.');
    }
    return data;
  },

  async sendEppCode(domainLinkId: string): Promise<{ domain: string; message: string }> {
    const idempotencyKey = crypto.randomUUID();
    const { data, error } = await supabase.functions.invoke('domain-epp', {
      method: 'POST',
      body: { domain_link_id: domainLinkId, method: 'send' },
      headers: { 'idempotency-key': idempotencyKey },
    });
    if (error) {
      const body = (error as { context?: { body?: { message?: string } } }).context?.body;
      throw new Error(body?.message ?? (error as { message?: string }).message ?? 'EPP send failed.');
    }
    return data;
  },
};
